/**
 * VendaViewDialog Component - Visualização de Venda
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Venda } from "@/types/venda";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface VendaViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venda: Venda | null;
}

export function VendaViewDialog({
  open,
  onOpenChange,
  venda,
}: VendaViewDialogProps) {
  if (!venda) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("pt-BR");
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      Pago: "default",
      Pendente: "secondary",
      Cancelado: "destructive",
    };
    return variants[status] || "default";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalhes da Venda #{venda.numero_venda}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações Gerais */}
          <div>
            <h3 className="font-semibold mb-3">Informações Gerais</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Data:</span>
                <p className="font-medium">{formatDate(venda.data_venda)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Cliente:</span>
                <p className="font-medium">{venda.cliente?.nome || "Cliente avulso"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Estabelecimento:</span>
                <p className="font-medium">{venda.estabelecimento?.nome || "-"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Responsável:</span>
                <p className="font-medium">{venda.responsavel_venda || "-"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Forma de Pagamento:</span>
                <p className="font-medium">{venda.forma_pagamento}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>
                <div className="mt-1">
                  <Badge variant={getStatusBadge(venda.status_pagamento)}>
                    {venda.status_pagamento}
                  </Badge>
                </div>
              </div>
              {venda.canal_venda && (
                <div>
                  <span className="text-muted-foreground">Canal de Venda:</span>
                  <p className="font-medium">{venda.canal_venda}</p>
                </div>
              )}
              {venda.numero_pedido && (
                <div>
                  <span className="text-muted-foreground">Nº Pedido:</span>
                  <p className="font-medium">{venda.numero_pedido}</p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Itens da Venda */}
          <div>
            <h3 className="font-semibold mb-3">Itens da Venda</h3>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead className="text-right">Qtd</TableHead>
                    <TableHead className="text-right">Preço Unit.</TableHead>
                    <TableHead className="text-right">Desconto</TableHead>
                    <TableHead className="text-right">Subtotal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {venda.itens.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.produto_nome}</div>
                          {item.brinde && (
                            <Badge variant="secondary" className="mt-1">
                              Brinde
                            </Badge>
                          )}
                          {item.observacoes_item && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {item.observacoes_item}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {item.quantidade} {item.unidade_medida}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(item.preco_unitario)}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.desconto_percentual > 0 && (
                          <div>{item.desconto_percentual}%</div>
                        )}
                        {item.desconto_valor > 0 && (
                          <div>{formatCurrency(item.desconto_valor)}</div>
                        )}
                        {item.desconto_percentual === 0 && item.desconto_valor === 0 && "-"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.subtotal)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <Separator />

          {/* Totais */}
          <div>
            <h3 className="font-semibold mb-3">Totais</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-medium">{formatCurrency(venda.subtotal)}</span>
              </div>
              {venda.desconto_total > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Desconto:</span>
                  <span className="font-medium text-destructive">
                    -{formatCurrency(venda.desconto_total)}
                  </span>
                </div>
              )}
              {venda.valor_frete > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Frete:</span>
                  <span className="font-medium">{formatCurrency(venda.valor_frete)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span>{formatCurrency(venda.valor_total)}</span>
              </div>
            </div>
          </div>

          {/* Entrega */}
          {(venda.data_entrega || venda.situacao_entrega) && (
            <>
              <Separator />
              <div>
                <h3 className="font-semibold mb-3">Entrega</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {venda.data_entrega && (
                    <div>
                      <span className="text-muted-foreground">Data de Entrega:</span>
                      <p className="font-medium">{formatDate(venda.data_entrega)}</p>
                    </div>
                  )}
                  {venda.situacao_entrega && (
                    <div>
                      <span className="text-muted-foreground">Situação:</span>
                      <p className="font-medium">{venda.situacao_entrega}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Observações */}
          {(venda.observacoes || venda.observacoes_gerais) && (
            <>
              <Separator />
              <div>
                <h3 className="font-semibold mb-3">Observações</h3>
                {venda.observacoes && (
                  <div className="mb-2">
                    <span className="text-sm text-muted-foreground">Observações:</span>
                    <p className="text-sm mt-1">{venda.observacoes}</p>
                  </div>
                )}
                {venda.observacoes_gerais && (
                  <div>
                    <span className="text-sm text-muted-foreground">Observações Gerais:</span>
                    <p className="text-sm mt-1">{venda.observacoes_gerais}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
